<!--
  One approvable plan item. Approving records the decision; turning it into a real campaign is Phase 3, so
  the card is deliberately honest about that rather than implying a campaign already exists.
-->
<template>
  <v-card
    outlined
    class="fill-height d-flex flex-column"
    :class="{ 'dismissed': isDismissed, 'highlight': highlight }"
  >
    <v-card-title class="text-subtitle-2 align-start">
      <v-icon
        small
        class="mr-2 mt-1"
      >
        {{ typeMeta.icon }}
      </v-icon>
      <span class="flex-grow-1">{{ item.title }}</span>
      <v-chip
        x-small
        :color="statusMeta.color"
        dark
        class="ml-2 flex-shrink-0"
      >
        {{ statusMeta.label }}
      </v-chip>
    </v-card-title>

    <v-card-subtitle class="pb-1">
      <span class="text-caption">{{ typeMeta.label }}</span>
      <span
        v-if="platformNames"
        class="text-caption"
      > · {{ platformNames }}</span>
      <span
        v-if="whenLabel"
        class="text-caption"
      > · {{ whenLabel }}</span>
    </v-card-subtitle>

    <v-card-text class="flex-grow-1 pt-0">
      <div
        v-if="item.rationale"
        class="text-caption grey--text"
      >
        {{ item.rationale }}
      </div>
      <div
        v-if="detail.goal"
        class="text-caption mt-2"
      >
        {{ detail.goal }}
      </div>

      <!--
        Where this item sends people. Worth showing on the card because it is the one part of a campaign a
        marketer can sanity-check at a glance — a pin about postcards pointed at the profession's landing page
        is a mistake you can only see if the destination is visible before it is built.
      -->
      <div
        v-if="detail.destinationUrl"
        class="text-caption mt-2 d-flex align-center"
      >
        <v-icon
          x-small
          class="mr-1"
        >
          mdi-link-variant
        </v-icon>
        <a
          :href="detail.destinationUrl"
          target="_blank"
          rel="noopener"
          :title="detail.destinationUrl"
        >{{ detail.destinationLabel || detail.destinationUrl }}</a>
      </div>

      <!--
        The post this item exists to push. Shown as its own line rather than folded into the destination,
        because it is a dependency and not just a link: until that post is live this item cannot be built at
        all, and the marketer needs to see the order of work before they click Build and get told.
      -->
      <div
        v-if="detail.promotesItemId"
        class="text-caption mt-2 d-flex align-start"
        :class="{ 'orange--text text--darken-3': !!promotesBlockedReason }"
      >
        <v-icon
          x-small
          class="mr-1 mt-1"
          :color="promotesBlockedReason ? 'orange darken-3' : ''"
        >
          {{ promotesBlockedReason ? 'mdi-clock-outline' : 'mdi-post-outline' }}
        </v-icon>
        <span>
          Promotes
          <a
            v-if="detail.promotesUrl"
            :href="detail.promotesUrl"
            target="_blank"
            rel="noopener"
          >{{ detail.promotesTitle }}</a>
          <template v-else>{{ detail.promotesTitle || 'a post that has left this plan' }}</template>
          <span v-if="promotesBlockedReason"> — {{ promotesBlockedReason }}</span>
        </span>
      </div>

      <!--
        The designs this campaign points at, and how far along they are. Approving the item already started
        them, so this is a status line first and an action second: Generate only shows before anything was
        fired, and the review link only once there is something a human can act on.
      -->
      <div
        v-if="template"
        class="template-ask mt-3 pa-2"
        :class="{ 'dismissed': templateDismissed }"
      >
        <div class="d-flex align-center">
          <v-icon
            small
            class="mr-2"
            :color="templateStatusMeta.color || 'grey'"
          >
            mdi-palette-swatch
          </v-icon>
          <span class="text-caption font-weight-medium flex-grow-1">{{ templateLabel }}</span>
          <v-chip
            x-small
            :color="templateStatusMeta.color"
            :outlined="!templateStatusMeta.color"
            :dark="!!templateStatusMeta.color"
            :title="templateChipTitle"
          >
            {{ templateStatusMeta.label }}
          </v-chip>

          <!--
            Only before the batches exist: once designs are being made there is nothing left to decline, and
            hiding the run would only cost the review link. Dismissing here leaves the ITEM approvable — the
            campaign is still worth building when the designs behind it aren't.
          -->
          <v-btn
            v-if="templateNotStarted"
            icon
            x-small
            class="ml-1"
            title="Skip these designs — approving this item won't start them"
            :disabled="dismissingTemplate || building || generatingTemplates"
            @click="setTemplateDismissed(true)"
          >
            <v-icon x-small>
              mdi-close
            </v-icon>
          </v-btn>
          <v-btn
            v-else-if="templateDismissed"
            icon
            x-small
            class="ml-1"
            title="Put these designs back"
            :disabled="dismissingTemplate || building"
            @click="setTemplateDismissed(false)"
          >
            <v-icon x-small>
              mdi-undo
            </v-icon>
          </v-btn>
        </div>

        <div
          v-if="template.rationale"
          class="text-caption grey--text mt-1"
        >
          {{ template.rationale }}
        </div>

        <v-btn
          v-if="templateNotStarted"
          x-small
          text
          color="mcsprimary"
          class="mt-1 px-0"
          :loading="generatingTemplates"
          :disabled="busy || building"
          title="Starts the designs now. Approving this item does the same thing — use this only if that didn't take."
          @click="generateTemplates"
        >
          Generate designs
        </v-btn>

        <v-btn
          v-else-if="template.reviewUrl"
          x-small
          text
          color="mcsprimary"
          class="mt-1 px-0"
          title="Opens the Template Designer's Catalog queue, where these get published or discarded"
          @click="openTemplateReview"
        >
          Review {{ template.reviewCount }} design{{ template.reviewCount === 1 ? '' : 's' }}
          <v-icon
            right
            x-small
          >
            mdi-open-in-new
          </v-icon>
        </v-btn>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-btn
        v-if="item.campaignId"
        small
        text
        color="mcsprimary"
        @click="openCampaign"
      >
        Open campaign
        <v-icon
          right
          x-small
        >
          mdi-open-in-new
        </v-icon>
      </v-btn>

      <!--
        The item produced a page, so there is nothing left to build here — the remaining work (review, then
        publish) happens on the content screens. The link is the whole affordance: unpublished it opens the
        draft preview, published it opens the live page.
      -->
      <template v-else-if="item.contentUrl">
        <v-btn
          small
          text
          color="mcsprimary"
          :title="contentLinkTitle"
          @click="openContent"
        >
          {{ contentLinkLabel }}
          <v-icon
            right
            x-small
          >
            mdi-open-in-new
          </v-icon>
        </v-btn>

        <v-spacer />

        <!--
          Written doesn't mean wanted. A draft nobody intends to publish would otherwise sit on the plan with
          no way off it — the only buttons this branch had were a link to the thing you'd decided against.
          The PAGE is left alone on purpose: it lives on the Content screens and is deleted there, which
          unlinks it from here anyway. This is the plan's decision, not the page's.
        -->
        <v-btn
          v-if="!isDismissed"
          icon
          small
          :title="contentDismissTitle"
          :disabled="busy"
          @click="setStatus($enums.AUDIENCE.ITEM_STATUS_IDS.DISMISSED)"
        >
          <v-icon small>
            mdi-close
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          icon
          small
          title="Put back"
          :disabled="busy"
          @click="setStatus($enums.AUDIENCE.ITEM_STATUS_IDS.SUGGESTED)"
        >
          <v-icon small>
            mdi-undo
          </v-icon>
        </v-btn>
      </template>

      <template v-else>
        <!--
          Disabled while the post this item promotes isn't live: the campaign's whole call to action is that
          post's url, so building one now would either link nowhere or link to a draft preview. The server
          refuses it too — this just stops the click that was always going to be refused, and the reason is
          already on the card above.
        -->
        <v-btn
          small
          color="primary"
          :loading="building"
          :disabled="busy || !!promotesBlockedReason"
          :title="buildTitle"
          @click="build"
        >
          {{ buildLabel }}
        </v-btn>

        <v-btn
          v-if="isApproved"
          small
          text
          :loading="busy"
          :disabled="building"
          @click="setStatus($enums.AUDIENCE.ITEM_STATUS_IDS.SUGGESTED)"
        >
          Undo
        </v-btn>

        <v-spacer />

        <v-btn
          v-if="!isDismissed"
          icon
          small
          title="Dismiss — a redraft won't propose it again"
          :disabled="busy"
          @click="setStatus($enums.AUDIENCE.ITEM_STATUS_IDS.DISMISSED)"
        >
          <v-icon small>
            mdi-close
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          icon
          small
          title="Put back"
          :disabled="busy"
          @click="setStatus($enums.AUDIENCE.ITEM_STATUS_IDS.SUGGESTED)"
        >
          <v-icon small>
            mdi-undo
          </v-icon>
        </v-btn>
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
import { platformName, planItemStatus } from '@/helpers/marketing';

export default {
  name: 'AudienceSuggestionCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    // Momentarily ringed because the marketer just arrived here from this item's touch on the plan screen.
    highlight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      busy: false,
      building: false,
      generatingTemplates: false,
      dismissingTemplate: false
    };
  },
  computed: {
    detail() {
      return this.item.detail || {};
    },
    typeMeta() {
      return (
        this.$enums.AUDIENCE.ITEM_TYPE[this.item.itemType] || {
          label: 'Item',
          icon: 'mdi-dots-horizontal'
        }
      );
    },
    // Shared with the plan screen's cadence, so a step and its item can never disagree about the state.
    statusMeta() {
      return planItemStatus(this.item);
    },
    contentLinkLabel() {
      return this.item.contentPublished ? 'View live page' : 'Preview';
    },
    contentLinkTitle() {
      return this.item.contentPublished
        ? 'Opens the published page'
        : 'Opens the draft — publish it from the Content pages screen';
    },
    // Says what dismissing does NOT do, because the page is the visible half of this item and leaving people
    // to guess whether this button deletes it is the reason they wouldn't press it.
    contentDismissTitle() {
      const kept = this.item.contentPublished
        ? 'The page stays published'
        : 'The draft page is kept — delete it from the Content pages screen';
      return `Remove from this plan — a redraft won't propose it again. ${kept}.`;
    },
    isApproved() {
      return this.item.status === this.$enums.AUDIENCE.ITEM_STATUS_IDS.APPROVED;
    },
    isDismissed() {
      return this.item.status === this.$enums.AUDIENCE.ITEM_STATUS_IDS.DISMISSED;
    },
    platformNames() {
      return (this.detail.platforms || []).map((p) => platformName(p)).join(', ');
    },
    // Pages and template batches are generated in the background, not turned into campaigns.
    isGeneratedContent() {
      return this.item.itemType === 3 || this.item.itemType === 5;
    },
    // A blog post is written inline and comes back with a preview link, so it gets its own wording — calling it
    // manual (as it once was, before there was a generator) undersells what the button does.
    isBlogPost() {
      return this.item.itemType === 4;
    },
    // Pinterest boards and "other" have no generator at all — approving is the whole action, so the button
    // must not promise to build something.
    isManualItem() {
      return this.item.itemType === 6 || this.item.itemType === 7;
    },
    buildLabel() {
      if (this.isBlogPost) return 'Write post';
      if (this.isGeneratedContent) return 'Generate';
      if (this.isManualItem) return 'Approve';
      return this.isApproved ? 'Build campaign' : 'Approve & build';
    },
    // Why this item can't be built yet, when it is the launch half of a blog play. Empty when it promotes
    // nothing, or when the post it promotes is live — the only state in which the campaign has a real url to
    // point at. Mirrors what AudiencePlanService refuses on, so the card and the server agree.
    promotesBlockedReason() {
      if (!this.detail.promotesItemId) return '';
      if (!this.detail.promotesTitle) return 'that post is no longer on this plan';
      if (!this.detail.promotesUrl) return 'write that post first';
      if (!this.detail.promotesPublished) return 'publish that post first';
      return '';
    },
    buildTitle() {
      if (this.promotesBlockedReason) {
        return `Can't be built yet — ${this.promotesBlockedReason}. This campaign links to the live post.`;
      }
      if (this.isBlogPost) return 'Writes the post now, then hands you a preview link to review';
      if (this.isGeneratedContent) return 'Starts the content generator in the background';
      if (this.isManualItem) return 'Records the decision — this one is written by hand';
      return 'Creates the campaign and generates its content. The send date comes from the weekly caps.';
    },
    // The designs this item asked for. Null on most items — a template ask is optional, and only drafted
    // when the audience is genuinely thin on the product the campaign points at.
    template() {
      return this.detail.template || null;
    },
    // Derived server-side from the batches' jobs, so it can't go stale the way a stored status would —
    // designs are published and discarded on the Template Designer, which knows nothing about plans.
    templateStatusMeta() {
      const status = this.template ? this.template.status : 0;
      return (
        this.$enums.AUDIENCE.TEMPLATE_STATUS[status] || { label: 'Suggested', color: '' }
      );
    },
    templateNotStarted() {
      return (
        !!this.template &&
        this.template.status === this.$enums.AUDIENCE.TEMPLATE_STATUS_IDS.SUGGESTED
      );
    },
    templateDismissed() {
      return (
        !!this.template &&
        this.template.status === this.$enums.AUDIENCE.TEMPLATE_STATUS_IDS.DISMISSED
      );
    },
    templateLabel() {
      if (!this.template) return '';
      const scope = this.template.designGroupName || 'this audience';
      return `${this.template.count} × ${this.template.categoryName || 'designs'} · ${scope}`;
    },
    templateChipTitle() {
      if (!this.template) return '';

      const ids = this.$enums.AUDIENCE.TEMPLATE_STATUS_IDS;
      if (this.template.status === ids.SUGGESTED) {
        return 'Starts automatically when you approve this item';
      }
      if (this.template.status === ids.DISMISSED) {
        return 'Skipped — approving this item builds the campaign without them';
      }

      const when = this.template.firedDate
        ? new Date(this.template.firedDate).toLocaleString()
        : null;
      const started = when ? `Started ${when}. ` : '';

      if (this.template.status === ids.GENERATING) {
        return `${started}Still being generated${
          this.template.reviewCount ? ` — ${this.template.reviewCount} ready to review already` : ''
        }.`;
      }
      if (this.template.status === ids.UNDER_REVIEW) {
        return `${started}Waiting for someone to publish or discard them.`;
      }
      return `${started}Nothing left to review — these have all been published or discarded.`;
    },
    whenLabel() {
      if (this.item.suggestedDate) {
        return new Date(this.item.suggestedDate).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric'
        });
      }
      return this.detail.dayOffset ? `Day ${this.detail.dayOffset}` : '';
    }
  },
  methods: {
    // New tab on purpose: the admin is working through a plan, and navigating away loses their place in it.
    openCampaign() {
      const href = this.$router.resolve(`/marketing/campaigns/${this.item.campaignId}`).href;
      window.open(href, '_blank', 'noopener');
    },
    // Already absolute (front-end web root), so it goes straight to window.open rather than through the router.
    openContent() {
      window.open(this.item.contentUrl, '_blank', 'noopener');
    },
    // The Template Designer is a separate app on its own subdomain, so this is an absolute url from the
    // server rather than a route — same treatment a "link" content-gap step gets.
    openTemplateReview() {
      window.open(this.template.reviewUrl, '_blank', 'noopener');
    },
    // Inline, not fire-and-forget: the response carries the send date the SCHEDULER assigned, which is the
    // thing the admin most needs to see and can't predict from the plan.
    build() {
      this.building = true;
      this.$store
        .dispatch('audiences/realizeItem', this.item.id)
        .then((result) => {
          // The designs are a separate ask that rides along with the build, so its outcome is reported
          // separately: they can fail (usually the brand gate) while the campaign itself is fine.
          const designs = result.templatesStarted
            ? ` · ${result.templatesStarted} design ${result.templatesStarted === 1 ? 'batch' : 'batches'} started`
            : '';

          if (result.error) this.$root.$snackbarError(result.error);
          // A note means "accepted, but there was nothing to build" — success, not failure.
          else if (result.note) this.$root.$snackbarSuccess(result.note + designs);
          else if (result.wasAlreadyRealized) this.$root.$snackbarSuccess('Already built.' + designs);
          else if (result.scheduledSend) {
            this.$root.$snackbarSuccess(
              'Built — scheduled for ' + new Date(result.scheduledSend).toLocaleString() + designs
            );
          } else if (this.isBlogPost) {
            // The post is written by the time this resolves, so "runs in the background" would be wrong.
            this.$root.$snackbarSuccess('Post written — use Preview to review it.' + designs);
          } else {
            this.$root.$snackbarSuccess('Started — this runs in the background.' + designs);
          }

          // Deliberately after the success toast, and not an error: the item WAS built. Only the designs
          // behind it weren't, and the card keeps its Generate button so it can be retried.
          if (result.templateError) this.$root.$snackbarError(result.templateError);

          this.$emit('changed');
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.building = false;
        });
    },
    // The retry path. Safe to click repeatedly — the server refuses an ask that already has its batches.
    generateTemplates() {
      this.generatingTemplates = true;
      this.$store
        .dispatch('audiences/runItemTemplate', this.item.id)
        .then((result) => {
          if (result.templatesStarted) {
            this.$root.$snackbarSuccess(
              `${result.templatesStarted} design ${result.templatesStarted === 1 ? 'batch' : 'batches'} started.`
            );
          } else {
            this.$root.$snackbarSuccess('Those designs were already started.');
          }
          this.$emit('changed');
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.generatingTemplates = false;
        });
    },
    // Only the designs — the item keeps its own status, so a dismissed ask still leaves an approvable campaign.
    setTemplateDismissed(dismissed) {
      this.dismissingTemplate = true;
      this.$store
        .dispatch('audiences/setItemTemplateDismissed', { itemId: this.item.id, dismissed })
        .then(() => this.$emit('changed'))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.dismissingTemplate = false;
        });
    },
    setStatus(status) {
      this.busy = true;
      this.$store
        .dispatch('audiences/setItemStatus', { itemId: this.item.id, status })
        .then(() => this.$emit('changed'))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.busy = false;
        });
    }
  }
};
</script>

<style scoped>
.dismissed {
  opacity: 0.55;
}

/* Fades out on its own (the parent drops the flag) — see Suggestions.reveal for why it isn't sticky. */
.highlight {
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 2px var(--v-primary-base);
  transition: box-shadow 0.4s ease, border-color 0.4s ease;
}

/* Set apart from the rationale above it: the designs are a second, separate thing this item asks for. */
.template-ask {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}
</style>
